import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router';
import { FiCalendar, FiClock, FiEdit3 } from 'react-icons/fi';
import { PortableText } from '@portabletext/react';
import { client, urlFor } from '../../sanity/client';
import SEO from '../SEO/SEO';
import { ArticleSchema, BreadcrumbSchema } from '../SEO/StructuredData';

interface PostDetail {
  title: string;
  mainImage: any;
  body: any;
  publishedAt: string;
  author: string;
  excerpt: string;
}

type IconComponent = React.ComponentType<{ className?: string }>;
const CalendarIcon = FiCalendar as IconComponent;
const AuthorIcon = FiEdit3 as IconComponent;
const ClockIcon = FiClock as IconComponent;

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<PostDetail | null>(null);

  useEffect(() => {
    if (!slug) return;

    const query = `*[_type == "post" && slug.current == $slug][0] {
  title,
  mainImage,
  body,
  publishedAt,
  author,
  excerpt
}`;

    client
      .fetch(query, { slug })
      .then((data: PostDetail) => setPost(data))
      .catch(console.error);
  }, [slug]);

  if (!post) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-slate-50 px-6 pt-28">
        <div className="rounded-xl border border-slate-200 bg-white px-6 py-4 text-sm text-slate-600 shadow-sm">
          Loading article...
        </div>
      </div>
    );
  }

  const publishedLabel = new Date(post.publishedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const getReadingTime = () => {
    if (!Array.isArray(post.body)) return '2 min read';

    const text = post.body
      .flatMap((block: any) => (Array.isArray(block?.children) ? block.children : []))
      .map((child: any) => child?.text || '')
      .join(' ')
      .trim();

    const words = text ? text.split(/\s+/).length : 0;
    const mins = Math.max(2, Math.ceil(words / 220));
    return `${mins} min read`;
  };

  const ptComponents = {
    types: {
      image: ({ value }: any) => {
        if (!value?.asset?._ref) return null;
        return (
          <figure className="my-8 overflow-hidden rounded-xl border border-slate-200">
            <img
              src={urlFor(value).fit('max').auto('format').url()}
              alt={value.alt || 'Blog content visual'}
              className="h-auto w-full object-cover"
            />
          </figure>
        );
      },
    },
    block: {
      h2: ({ children }: any) => <h2 className="mt-10 text-2xl font-bold text-slate-900">{children}</h2>,
      h3: ({ children }: any) => <h3 className="mt-8 text-xl font-semibold text-slate-900">{children}</h3>,
      normal: ({ children }: any) => <p className="mt-5 text-base leading-8 text-slate-700">{children}</p>,
      blockquote: ({ children }: any) => (
        <blockquote className="my-8 border-l-4 border-teal-500 bg-teal-50/40 px-5 py-3 text-slate-700">
          {children}
        </blockquote>
      ),
    },
    list: {
      bullet: ({ children }: any) => <ul className="mt-4 list-disc space-y-2 pl-6 text-slate-700">{children}</ul>,
      number: ({ children }: any) => <ol className="mt-4 list-decimal space-y-2 pl-6 text-slate-700">{children}</ol>,
    },
    marks: {
      link: ({ children, value }: any) => (
        <a
          href={value?.href}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-teal-700 underline decoration-teal-400 underline-offset-4"
        >
          {children}
        </a>
      ),
    },
  };

  return (
    <article className="bg-slate-50 pb-16 sm:pb-20">
      <SEO
        title={post.title}
        description={post.excerpt || post.title}
        ogType="article"
        ogImage={post.mainImage ? urlFor(post.mainImage).width(1200).height(630).url() : undefined}
        article={{ publishedTime: post.publishedAt, author: post.author || 'Cool & Care Team' }}
      />
      <ArticleSchema
        title={post.title}
        description={post.excerpt || post.title}
        url={`/blogs/${slug}`}
        image={post.mainImage ? urlFor(post.mainImage).url() : undefined}
        publishedAt={post.publishedAt}
        author={post.author || 'Cool & Care Team'}
      />
      <BreadcrumbSchema items={[{ name: 'Blog', url: '/blogs' }, { name: post.title, url: `/blogs/${slug}` }]} />

      <section className="bg-[#0A1628] py-24 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="flex justify-start">
            <Link
              to="/blogs"
              className="inline-flex items-center rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm font-medium text-teal-200 transition hover:border-teal-300/50 hover:bg-teal-400/10 hover:text-teal-100"
            >
              Back to Blogs
            </Link>
          </div>

          <div className="text-center">
            <p className="text-teal-400 text-xs font-bold uppercase tracking-[0.2em] mb-4">Blog</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">{post.title}</h1>
          {post.excerpt && (
            <p className="text-white/40 mt-5 max-w-2xl mx-auto text-lg font-light">
              {post.excerpt}
            </p>
          )}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-sm text-white/80">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5">
                <CalendarIcon className="h-4 w-4 text-teal-400" />
                <span>{publishedLabel}</span>
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5">
                <AuthorIcon className="h-4 w-4 text-teal-400" />
                <span>{post.author || 'Cool & Care Team'}</span>
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5">
                <ClockIcon className="h-4 w-4 text-teal-400" />
                <span>{getReadingTime()}</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-6 pt-8 sm:pt-10">
        {post.mainImage && (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            <img
              src={urlFor(post.mainImage).width(1400).height(820).fit('crop').url()}
              alt={post.title}
              className="h-auto w-full object-cover"
            />
          </div>
        )}

        <div className="prose prose-slate mt-8 max-w-none prose-headings:tracking-tight">
          <PortableText value={post.body} components={ptComponents} />
        </div>
      </div>
    </article>
  );
};

export default BlogPost;
